import type { IColRange } from './visibleCols';
import type { IProgressMatrix } from './progressMatrix';

/** Fixed row geometry. Must agree with the CSS: .colHead min-height (130),
 *  .floorCell min-height (56); zone tracks are pinned to 37px BY the grid
 *  template (content renders ~36.6px and stretches to fill the track).
 *  HEADER_ROW_H sizes the top pane (corner + header strip) — the header is
 *  OUTSIDE the body scroller, so it never enters the row-windowing math. */
export const HEADER_ROW_H = 130;
export const FLOOR_ROW_H = 56;
export const ZONE_ROW_H = 37;
const BUFFER_ROWS = 6;

/** Pre-measurement seed, mirroring SEED_COLS: covers any realistic viewport
 *  (30 rows ≈ 1,110–1,680px of body). */
export const SEED_ROWS = 30;

/** One body row of the matrix, in render order. */
export interface ITrackerRow {
  kind: 'floor' | 'zone';
  /** Index into matrix.floors. */
  floorIndex: number;
  /** Index into floors[floorIndex].zones — zone rows only. */
  zoneIndex?: number;
  /** Stable key: floorId for floor rows, `${floorId}::${zoneId}` for zone rows. */
  key: string;
}

export interface IRowGeometry {
  rows: ITrackerRow[];
  /** Body-pane content-y of each row's TOP (tops[0] === 0 — the header is a
   *  separate pane, not part of the scroller's coordinates). */
  tops: number[];
  /** tops[last] + its height — the body pane's total scrollHeight. */
  totalHeight: number;
  /** Explicit body tracks — the label/body panes' full gridTemplateRows:
   *  e.g. "56px 37px 37px 56px …". */
  templateRows: string;
}

/**
 * Every floor contributes exactly one floor row; its zone rows follow ONLY
 * when the floor isn't collapsed. One pass builds `rows`, the prefix-summed
 * `tops` (from 0 — body-pane coordinates) and `templateRows` together, so
 * the three stay in lockstep by construction.
 */
export function buildRowGeometry(
  floors: IProgressMatrix['floors'],
  collapsed: Record<string, boolean>,
): IRowGeometry {
  const rows: ITrackerRow[] = [];
  const tops: number[] = [];
  const templateParts: string[] = [];
  let y = 0;

  const pushRow = (row: ITrackerRow, height: number) => {
    rows.push(row);
    tops.push(y);
    templateParts.push(`${height}px`);
    y += height;
  };

  floors.forEach((floor, floorIndex) => {
    pushRow({ kind: 'floor', floorIndex, key: floor.floorId }, FLOOR_ROW_H);
    if (!collapsed[floor.floorId]) {
      floor.zones.forEach((zone, zoneIndex) => {
        pushRow({ kind: 'zone', floorIndex, zoneIndex, key: `${floor.floorId}::${zone.zoneId}` }, ZONE_ROW_H);
      });
    }
  });

  return { rows, tops, totalHeight: y, templateRows: templateParts.join(' ') };
}

/** Greatest index i with `tops[i] <= y`, defaulting to 0 when no index
 *  qualifies (y below the first row) — there's no valid "row before the
 *  first" to point at, so clamping to the first row is the right answer. */
function greatestTopAtMost(tops: readonly number[], y: number): number {
  let lo = 0;
  let hi = tops.length - 1;
  let ans = 0;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (tops[mid]! <= y) {
      ans = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return ans;
}

/** Greatest index i with `tops[i] < y` (same default-0 rationale as above). */
function greatestTopBelow(tops: readonly number[], y: number): number {
  let lo = 0;
  let hi = tops.length - 1;
  let ans = 0;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (tops[mid]! < y) {
      ans = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return ans;
}

/** Inclusive [start, end] BODY-row indices to render, buffered. Mirrors
 *  visibleColRange, in the body pane's own scroll coordinates: the header is
 *  a separate pane outside the scroller, so nothing overlays the viewport
 *  and both query points (`scrollTop`, `scrollTop + clientHeight`) are used
 *  as-is. Variable row heights make this a binary search over `tops` instead
 *  of a division. Same over-scroll clamp semantics (rowCount === 0 stays
 *  deliberately inverted).
 *
 *  `totalHeight` clamps both query points to the known content bound before
 *  searching: a stale scrollTop can momentarily overshoot a freshly-shrunk
 *  totalHeight (e.g. right after collapsing floors, before the scroller's own
 *  scrollTop has re-clamped) — the same over-scroll scenario visibleColRange
 *  guards via its `Math.min(start, end)` result clamp, applied here at the
 *  query instead. */
export function visibleRowRange(
  scrollTop: number,
  clientHeight: number,
  tops: readonly number[],
  totalHeight: number,
): IColRange {
  const rowCount = tops.length;
  const from = Math.min(Math.max(scrollTop, 0), totalHeight);
  const to = Math.min(Math.max(scrollTop + clientHeight, 0), totalHeight);
  const firstVisible = greatestTopAtMost(tops, from);
  const lastVisible = greatestTopBelow(tops, to);
  const start = Math.max(0, firstVisible - BUFFER_ROWS);
  const end = Math.min(rowCount - 1, lastVisible + BUFFER_ROWS);
  return {
    start: rowCount === 0 ? start : Math.min(start, end),
    end,
  };
}

/** The seed window for `rowCount` rows (clamped; `rowCount === 0` yields the
 *  deliberately inverted empty range, same as visibleRowRange). */
export function seedRowRange(rowCount: number): IColRange {
  return { start: 0, end: Math.min(rowCount, SEED_ROWS) - 1 };
}
